import React, { useEffect, useState } from 'react';
import cx from 'classnames';
import { isNull, mapKeys, uniq } from 'lodash';
import moment from 'moment-timezone';
import { Waypoint } from 'react-waypoint';

import LoadingIndicator from '@magoosh/layouts/components/loading_indicator';
import { PrimaryButton } from '@magoosh/lib/button';
import Dropdown from '@magoosh/lib/dropdown';
import { FontAwesomeIcon } from '@magoosh/lib/icons';
import AnswerDifficulty from '@magoosh/organisms/answer_difficulty';
import AnswerFlag from '@magoosh/organisms/answer_flag';
import AnswerIcon from '@magoosh/organisms/answer_icon';
import NoValueDash from '@magoosh/organisms/no_value_dash';
import { paths } from 'config/path_helpers';
import { AnswerData } from 'types/answer';
import { secondsFormattedAsMinutes } from 'utilities/time';

import style from './style.module.scss';

interface Props {
  answers: AnswerData[];
  isLoading?: boolean;
  linkComponent?: React.ComponentType<any>;
  onLoadMoreAnswers?: () => void;
  pathForAnswer?: (answerId: number) => string;
}

export default (props: Props) => {
  const [isMobile, setIsMobile] = useState(false);

  const LinkComponent = props.linkComponent || 'a';
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles';

  const navigateToAnswer = (e: React.MouseEvent, answerId: number) => {
    const url = props.pathForAnswer ? props.pathForAnswer(answerId) : paths.answer(answerId, {});

    if (e.metaKey || e.ctrlKey) {
      window.open(url, '_blank');
    } else {
      window.location.href = url;
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  if (isMobile) {
    return (
      <div className="d-flex flex-column gap-s" style={{ padding: '20px 15px' }}>
        {
          props.answers.length > 0 || props.isLoading ? (
            <>
              {
                props.answers.map((answer, idx) => (
                  <React.Fragment key={answer.id}>
                    {
                      idx === props.answers.length - 20 && !props.isLoading && props.onLoadMoreAnswers && (
                        <Waypoint onEnter={props.onLoadMoreAnswers} />
                      )
                    }

                    <div
                      className="d-flex flex-column gap-s"
                      onClick={(e) => navigateToAnswer(e, answer.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="align-items-center d-flex flex-1 gap-xs">
                        <div className="d-flex justify-content-center" style={{ width: 24 }}>
                          <AnswerIcon answer={answer} />
                        </div>

                        <div className="flex-1" style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          <strong>{answer.prompt.question.title}</strong>
                        </div>

                        <div className="small u-margin-L-s">
                          {moment(answer.createdAt * 1000).tz(timeZone).format('MMM D')}
                        </div>

                        <span onClick={e => e.stopPropagation()}>
                          <Dropdown align="right">
                            <Dropdown.Trigger>
                              <FontAwesomeIcon
                                className="text-gray u-padding-A-xxs"
                                icon="ellipsis-h"
                              />
                            </Dropdown.Trigger>

                            <Dropdown.Content>
                              <ul className="list-unstyled u-margin-A-n">
                                <li className="u-padding-H-s u-padding-V-xxs">
                                  <AnswerFlag
                                    flagged={answer.isFlagged}
                                    promptId={answer.prompt.id}
                                    showText
                                  />
                                </li>

                                {
                                  answer.scoreReport && (
                                    <li className="u-padding-H-s u-padding-V-xxs">
                                      <LinkComponent
                                        className="text-gray text-hover-no-decoration text-no-decoration"
                                        href={
                                          answer.scoreReport.requestType === 'essay' ? (
                                            paths.scoreReport(answer.scoreReport.id)
                                          ) : (
                                            paths.scoreReportAnswer(answer.scoreReport.id, answer.id)
                                          )
                                        }
                                      >
                                        Score Report
                                      </LinkComponent>
                                    </li>
                                  )
                                }

                                {
                                  answer.hasNote && (
                                    <li className="u-padding-H-s u-padding-V-xxs">
                                      <LinkComponent
                                        className="text-gray text-hover-no-decoration text-no-decoration"
                                        href={`${paths.answer(answer.id)}#note`}
                                        style={{ display: 'block', width: '100%' }}
                                      >
                                        View Note
                                      </LinkComponent>
                                    </li>
                                  )
                                }
                              </ul>
                            </Dropdown.Content>
                          </Dropdown>
                        </span>
                      </div>

                      <table className={cx(style.mobileTable, 'small')}>
                        <tr>
                          <td style={{ width: 120 }}>Section/Subject</td>
                          <td>{uniq([answer.examSection.name, ...answer.prompt.subjectTags]).join(', ')}</td>
                        </tr>

                        {
                          answer.humanDifficulty && (
                            <tr>
                              <td style={{ width: 120 }}>Difficulty</td>
                              <td><AnswerDifficulty answer={answer} /></td>
                            </tr>
                          )
                        }

                        {
                          !isNull(answer.time) && (
                            <tr>
                              <td style={{ width: 120 }}>Pace</td>
                              <td>{secondsFormattedAsMinutes(answer.time, false)}</td>
                            </tr>
                          )
                        }
                      </table>
                    </div>

                    {
                      idx !== props.answers.length - 1 && (
                        <hr className="u-margin-V-xxs" />
                      )
                    }
                  </React.Fragment>
                ))
              }

              {
                props.isLoading && (
                  <div className="d-flex justify-content-center">
                    <LoadingIndicator />
                  </div>
                )
              }

              {
                !props.isLoading && props.onLoadMoreAnswers && (
                  <div className="d-flex justify-content-center">
                    <PrimaryButton onClick={props.onLoadMoreAnswers}>
                      Show More Answers
                    </PrimaryButton>
                  </div>
                )
              }
            </>
          ) : (
            <div className="align-items-center d-flex flex-1 flex-column gap-s u-margin-V-m">
              No answers found.
            </div>
          )
        }
      </div>
    );
  } else {
    return (
      <table className={style.table}>
        <thead>
          <th>Question</th>
          <th className={style.colSection}>Section</th>
          <th className={style.colSubject}>Subject</th>
          <th className={cx(style.colDifficulty, style.noWrap)}>Difficulty</th>
          <th className={cx(style.colYourPace, style.noWrap, 'text-center')}>Your Pace</th>
          <th className={cx(style.colOthersPace, style.noWrap, 'text-center visible-lg')}>Others' Pace</th>
          <th className={cx(style.colDate, style.noWrap, 'text-center')}>Date</th>
          <th className={cx(style.colNote, style.noWrap, 'text-center visible-lg')}>Note</th>
          <th className={cx(style.colFlag, style.noWrap, 'text-center visible-lg')}>Flag</th>
        </thead>

        <tbody>
          {
            props.answers.length > 0 || props.isLoading ? (
              <>
                {
                  props.answers.map((answer, idx) => (
                    <React.Fragment key={answer.id}>
                      <tr
                        className={style.clickableRow}
                        onClick={(e) => navigateToAnswer(e, answer.id)}
                      >
                        <td>
                          {
                            idx === props.answers.length - 20 && !props.isLoading && props.onLoadMoreAnswers && (
                              <Waypoint onEnter={props.onLoadMoreAnswers} />
                            )
                          }

                          <div className="d-flex gap-s">
                            <div className="d-flex justify-content-center" style={{ minWidth: 24 }}>
                              <AnswerIcon answer={answer} />
                            </div>

                            <div className="d-flex flex-column">
                              <div>
                                <strong>{answer.prompt.question.title}</strong>
                              </div>

                              <div className="text-gray-light">
                                {
                                  answer.scoreReport && (
                                    <LinkComponent
                                      className="text-gray-light"
                                      href={
                                        answer.scoreReport.requestType === 'essay' ? (
                                          paths.scoreReport(answer.scoreReport.id)
                                        ) : ( 
                                          paths.scoreReportAnswer(answer.scoreReport.id, answer.id)
                                        )
                                      }
                                      onClick={e => e.stopPropagation()}
                                      style={{ color: '#2A6EB6' }}
                                    >
                                      Score Report
                                    </LinkComponent>
                                  )
                                }
                              </div>
                            </div>
                          </div>
                        </td>

                        <td>
                          {answer.examSection.name || <NoValueDash />}
                        </td>

                        <td>
                          {
                            answer.prompt.subjectTags.length > 0 ? (
                              answer.prompt.subjectTags.join(', ')
                            ) : <NoValueDash />
                          }
                        </td>

                        <td className={style.noWrap}>
                          <AnswerDifficulty answer={answer} />
                        </td>

                        <td className={cx(style.noWrap, 'text-center')}>
                          {
                            !isNull(answer.time) ? (
                              secondsFormattedAsMinutes(answer.time, false)
                            ) : <NoValueDash />
                          }
                        </td>

                        <td className={cx(style.noWrap, 'text-center visible-lg')}>
                          {
                            answer.prompt.correctAnswersAvgTime ? (
                              secondsFormattedAsMinutes(answer.prompt.correctAnswersAvgTime, false)
                            ) : <NoValueDash />
                          }
                        </td>

                        <td className={cx(style.noWrap, 'text-center')}>
                          {moment(answer.createdAt * 1000).tz(timeZone).format('MMM D')}
                        </td>

                        <td className={cx(style.noWrap, 'text-center visible-lg')} onClick={e => e.stopPropagation()}>
                          {
                            answer.hasNote ? (
                              <LinkComponent
                                className="text-gray text-hover-no-decoration text-no-decoration"
                                href={`${paths.answer(answer.id)}#note`}
                              >
                                <FontAwesomeIcon icon="pencil" />
                              </LinkComponent>
                            ) : (
                              <NoValueDash />
                            )
                          }
                        </td>

                        <td className={cx(style.noWrap, 'text-center visible-lg')} onClick={e => e.stopPropagation()}>
                          <AnswerFlag
                            className="justify-content-center"
                            flagged={answer.isFlagged}
                            promptId={answer.prompt.id}
                          />
                        </td>
                      </tr>
                    </React.Fragment>
                  ))
                }

                {
                  props.isLoading && (
                    <tr>
                      <td colSpan={9}>
                        <div className="d-flex justify-content-center">
                          <LoadingIndicator />
                        </div>
                      </td>
                    </tr>
                  )
                }

                {
                  !props.isLoading && props.onLoadMoreAnswers && (
                    <tr>
                      <td colSpan={9}>
                        <div className="d-flex justify-content-center">
                          <PrimaryButton onClick={props.onLoadMoreAnswers}>
                            Show More Answers
                          </PrimaryButton>
                        </div>
                      </td>
                    </tr>
                  )
                }
              </>
            ) : (
              <tr >
                <td colSpan={9}>
                  <div className="align-items-center d-flex flex-1 flex-column gap-s u-margin-V-m">
                    No answers found.
                  </div>
                </td>
              </tr>
            )
          }
        </tbody>
      </table>
    );
  }
};
